'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Package, Layers, KanbanSquare, Store, FileText, AlertTriangle, TrendingUp, Users, CreditCard, Clock, CheckCircle, Truck, ShoppingCart, ArrowRight, Activity, RefreshCw, Zap,  } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';


const ActivityChart = dynamic(() => import('./ActivityChart'), { ssr: false });

// Mock data — backend: query accounts_master_ledger for current user role + aggregate KPIs
const currentAccount = {
  id: 'acc-001',
  business_name: 'Grossiste Alaoui & Fils',
  account_role: 'Hybrid_Supplier_Retailer' as const,
  zone: 'Centre_Ville',
  phone: '+212 661-234567',
};

const recentActivity = [
  { id: 'act-001', type: 'order', text: 'Nouvelle commande — Snack Atlas Guéliz', amount: '1 240 MAD', time: 'Il y a 8 min', status: 'Pending_Review' as const, icon: ShoppingCart },
  { id: 'act-002', type: 'delivery', text: 'Livraison confirmée — Café Majorelle', amount: '3 180 MAD', time: 'Il y a 34 min', status: 'Delivered_Synced' as const, icon: CheckCircle },
  { id: 'act-003', type: 'expiry', text: 'Alerte expiration — Lot Fromage Frais #B-0441', amount: '5 jours restants', time: 'Il y a 1h', status: 'Pending_Review' as const, icon: AlertTriangle },
  { id: 'act-004', type: 'order', text: 'Commande en chargement — Restaurant Fès Marrakech', amount: '2 650 MAD', time: 'Il y a 1h 20min', status: 'Loading' as const, icon: Truck },
  { id: 'act-005', type: 'payment', text: 'Paiement reçu — Boulangerie Menara', amount: '890 MAD', time: 'Il y a 2h 15min', status: 'Delivered_Synced' as const, icon: CreditCard },
  { id: 'act-006', type: 'connection', text: 'Nouveau détaillant connecté — Épicerie Bab Jdid', amount: 'Crédit: 5 000 MAD', time: 'Il y a 3h', status: 'Active' as const, icon: Users },
];

const supplierQuickActions = [
  { id: 'qa-catalog', label: 'Gérer Catalogue', icon: Package, href: '/product-catalog-grid-view', color: 'btn-primary' },
  { id: 'qa-fifo', label: 'Vérifier Stock FIFO', icon: Layers, href: '/fifo-batch-inventory-manager', color: 'btn-secondary', badge: '5 alertes' },
  { id: 'qa-kanban', label: 'Traiter Commandes', icon: KanbanSquare, href: '/order-fulfillment-kanban-board', color: 'btn-primary', badge: '7 en attente' },
];

const retailerQuickActions = [
  { id: 'qa-marketplace', label: 'Parcourir Fournisseurs', icon: Store, href: '/multi-supplier-marketplace-view', color: 'btn-secondary' },
  { id: 'qa-order', label: 'Passer une Commande', icon: FileText, href: '/direct-order-sheet', color: 'btn-primary' },
];

const supplierKPIs = [
  { id: 'kpi-pending', label: 'Commandes En Attente', value: '7', subValue: 'À traiter aujourd\'hui', trend: 'up' as const, trendValue: '+3 depuis hier', variant: 'danger' as const, icon: <Clock size={18} /> },
  { id: 'kpi-expiring', label: 'Lots Expirant Bientôt', value: '5', subValue: '2 critiques ≤7j, 3 à ≤14j', trend: 'up' as const, trendValue: 'Action requise', variant: 'warning' as const, icon: <AlertTriangle size={18} /> },
  { id: 'kpi-connections', label: 'Détaillants Actifs', value: '23', subValue: '2 nouvelles connexions ce mois', trend: 'up' as const, trendValue: '+2 ce mois', variant: 'positive' as const, icon: <Users size={18} /> },
  { id: 'kpi-revenue', label: 'Chiffre du Jour', value: '14 820', subValue: 'MAD — 8 livraisons confirmées', trend: 'up' as const, trendValue: '+18% vs hier', variant: 'info' as const, icon: <TrendingUp size={18} /> },
];

const retailerKPIs = [
  { id: 'kpi-suppliers', label: 'Fournisseurs Connectés', value: '4', subValue: 'Tous actifs', variant: 'positive' as const, icon: <Store size={18} /> },
  { id: 'kpi-credit-total', label: 'Crédit Total Disponible', value: '28 500', subValue: 'MAD — sur 4 fournisseurs', variant: 'info' as const, icon: <CreditCard size={18} /> },
  { id: 'kpi-outstanding', label: 'Dettes En Cours', value: '9 340', subValue: 'MAD — 32.8% du crédit total', trend: 'down' as const, trendValue: '-5% cette semaine', variant: 'warning' as const, icon: <Activity size={18} /> },
  { id: 'kpi-orders-placed', label: 'Commandes Ce Mois', value: '12', subValue: '10 livrées, 2 en cours', variant: 'positive' as const, icon: <ShoppingCart size={18} /> },
];

export default function UnifiedLandingDashboardContent() {
  const [activeRole, setActiveRole] = useState<'supplier' | 'retailer'>('supplier');
  const isHybrid = currentAccount.account_role === 'Hybrid_Supplier_Retailer';

  const kpis = activeRole === 'supplier' ? supplierKPIs : retailerKPIs;

  return (
    <div className="space-y-6 fade-in">
      {/* Account header */}
      <div className="card-base flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center text-white text-xl font-800 flex-shrink-0">
          GA
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-lg font-700 text-foreground">{currentAccount.business_name}</h2>
            <StatusBadge status={currentAccount.account_role} />
          </div>
          <p className="text-sm text-muted-foreground">{currentAccount.zone.replace('_', ' ')} · Marrakech · {currentAccount.phone}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <RefreshCw size={12} />
            Mis à jour à 09:46
          </span>
        </div>
      </div>

      {/* Role tabs for hybrid */}
      {isHybrid && (
        <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit">
          <button
            onClick={() => setActiveRole('supplier')}
            className={`px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 flex items-center gap-2 ${
              activeRole === 'supplier' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package size={15} />
            Vue Fournisseur
          </button>
          <button
            onClick={() => setActiveRole('retailer')}
            className={`px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 flex items-center gap-2 ${
              activeRole === 'retailer' ?'bg-card text-primary shadow-card' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Store size={15} />
            Vue Détaillant
          </button>
        </div>
      )}

      {/* KPI grid — 4 cards, 2×2 or 4-col */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <MetricCard
            key={kpi.id}
            label={kpi.label}
            value={kpi.value}
            subValue={kpi.subValue}
            trend={kpi.trend}
            trendValue={kpi.trendValue}
            variant={kpi.variant}
            icon={kpi.icon}
          />
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {/* Activity chart */}
        <div className="xl:col-span-2 card-base">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-700 text-foreground">Volume de Commandes — 14 Derniers Jours</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Commandes reçues vs livrées</p>
            </div>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg">Mai 2026</span>
          </div>
          <ActivityChart />
        </div>

        {/* Quick actions */}
        <div className="card-base">
          <h3 className="text-sm font-700 text-foreground mb-4">
            {activeRole === 'supplier' ? 'Actions Fournisseur' : 'Actions Détaillant'}
          </h3>
          <div className="space-y-3">
            {(activeRole === 'supplier' ? supplierQuickActions : retailerQuickActions).map((action) => {
              const Icon = action.icon;
              return (
                <Link key={action.id} href={action.href}>
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all duration-150 cursor-pointer group">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      <Icon size={17} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-600 text-foreground">{action.label}</p>
                      {action.badge && (
                        <p className="text-xs text-danger font-600">{action.badge}</p>
                      )}
                    </div>
                    <ArrowRight size={15} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Dual role CTA for hybrid */}
          {isHybrid && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="section-header mb-3">Accès Rapide</p>
              <div className="grid grid-cols-2 gap-2">
                <Link href="/order-fulfillment-kanban-board">
                  <button className="w-full btn-primary text-xs py-2.5 justify-center">
                    <KanbanSquare size={13} />
                    Kanban
                  </button>
                </Link>
                <Link href="/direct-order-sheet">
                  <button className="w-full btn-accent text-xs py-2.5 justify-center">
                    <FileText size={13} />
                    Commander
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent activity feed */}
      <div className="card-base">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-700 text-foreground">Activité Récente</h3>
          <button className="text-xs text-primary font-600 hover:underline flex items-center gap-1">
            Voir tout <ArrowRight size={12} />
          </button>
        </div>
        <div className="divide-y divide-border">
          {recentActivity.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.id} className="flex items-start gap-3 py-3 hover:bg-muted/40 rounded-lg px-2 -mx-2 transition-colors">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  item.type === 'expiry' ? 'bg-warning/10 text-warning' :
                  item.type === 'delivery' ? 'bg-success/10 text-success' :
                  item.type === 'payment'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-500 truncate">{item.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.time}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-sm font-700 text-foreground font-tabular">{item.amount}</p>
                  <div className="mt-0.5">
                    <StatusBadge status={item.status} size="sm" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Alert banner */}
      <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl">
        <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-700 text-foreground">5 lots en alerte d'expiration</p>
          <p className="text-xs text-muted-foreground mt-0.5">2 lots expirent dans moins de 7 jours. Activez des remises immédiates pour éviter les pertes.</p>
        </div>
        <Link href="/fifo-batch-inventory-manager">
          <button className="btn-accent text-xs py-2 px-3 flex-shrink-0">
            <Zap size={13} />
            Voir Stock
          </button>
        </Link>
      </div>
    </div>
  );
}